import { createContext, useContext } from 'react';
import type { SystemTypographySettings } from '@/features/admin/types/typography';

/**
 * System Settings Interface
 * Matches backend SystemSettings model
 */
export interface SystemSettings {
  // Localization
  locale: string;
  rtlEnabled: boolean;

  // Branding
  loginLogoData: string | null;
  loginLogoSize: string;
  navbarLogoData: string | null;
  navbarLogoSize: string;
  appName: string;

  // Theme
  theme: string;

  // Colors
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  surfaceColor: string | null;
  textColor: string | null;
  textMutedColor: string | null;
  borderColor: string | null;

  // Typography
  themeFontFamily: string | null;
  themeFontSize: string;
  fontWeightHeading: string | null;
  fontWeightBody: string | null;

  // Layout
  layoutMode: string;
  borderRadius: string | null;
  borderWidth: string | null;
  shadowStyle: string | null;

  // Sizing (Customizable UI dimensions)
  baseFontSize: number;
  headingFontSize: number;
  smallFontSize: number;
  containerMaxWidth: number;
  containerPadding: number;
  cardPadding: number;
  cardHeaderPadding: number;
  cardBodyPadding: number;
  tableHeaderPaddingY: number;
  tableHeaderPaddingX: number;
  tableCellPaddingY: number;
  tableCellPaddingX: number;
  formInputPadding: number;
  buttonPadding: number;

  // Typography Settings (comprehensive system with 12 categories)
  typographySettings?: SystemTypographySettings;
}

/**
 * System Settings context value
 */
export interface SystemSettingsContextValue {
  /** Current system settings */
  settings: SystemSettings | null;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: Error | null;

  /** Refresh settings from API */
  refreshSettings: () => Promise<void>;
}

/**
 * System Settings context
 */
export const SystemSettingsContext = createContext<SystemSettingsContextValue | undefined>(undefined);

/**
 * Hook to access system settings context
 * @throws Error if used outside SystemSettingsProvider
 */
export const useSystemSettings = (): SystemSettingsContextValue => {
  const context = useContext(SystemSettingsContext);

  if (!context) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }

  return context;
};
